import { toast } from '@qse/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  activatePlan,
  createPlan,
  deactivatePlan,
  deletePlan,
  getMyQpaServices,
  getPlanById,
  getPlans,
  getPlanTenants,
  patchPlan,
} from '../plan'
import { queryKeys, queryOptions } from '../react-query-config'
import type { CreatePlanDto, PatchPlanDto, PlanResponseDto } from '../types'

export const usePlans = (filters?: Record<string, unknown>, options?: { enabled?: boolean }) => {
  const { enabled, ...restOptions } = options || {}

  return useQuery({
    queryKey: queryKeys.plans.list(filters),
    queryFn: () => getPlans(filters),
    enabled: enabled !== false, // Default to true if not specified
    ...queryOptions.plans, // Use specific plans query options
    ...restOptions,
  })
}

export const usePlanById = (id: string) => {
  return useQuery({
    queryKey: queryKeys.plans.detail(id),
    queryFn: () => getPlanById(id),
    ...queryOptions.plans,
    enabled: !!id,
  })
}

export const useCreatePlan = () => {
  const queryClient = useQueryClient()

  return useMutation<PlanResponseDto, Error, CreatePlanDto>({
    mutationFn: createPlan,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.lists() })
      toast.success(`Plan '${data.name}' has been created successfully`)
    },
  })
}

export const usePatchPlan = () => {
  const queryClient = useQueryClient()

  return useMutation<PlanResponseDto, Error, { id: string; data: PatchPlanDto }>({
    mutationFn: ({ id, data }) => patchPlan(id, data),
    onSuccess: (updatedPlan, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.detail(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.lists() })
      // Update the specific plan detail
      queryClient.setQueryData(queryKeys.plans.detail(id), updatedPlan)

      // Update all plan lists by updating the specific plan
      queryClient.setQueriesData({ queryKey: queryKeys.plans.lists() }, (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object') return oldData
        const data = oldData as { data?: PlanResponseDto[] }

        return {
          ...data,
          data:
            data.data?.map((plan: PlanResponseDto) =>
              plan.id === id ? { ...updatedPlan } : plan,
            ) || [],
        }
      })
      toast.success(`Plan '${updatedPlan.name}' has been updated successfully`)
    },
  })
}

export const useDeletePlan = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: deletePlan,
    onSuccess: (_, deletedId) => {
      // Update all plan lists by removing the deleted plan
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.lists() })
      queryClient.setQueriesData({ queryKey: queryKeys.plans.lists() }, (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object') return oldData
        const data = oldData as { data?: PlanResponseDto[]; pageInfo?: { endOffset?: number } }

        return {
          ...data,
          data: data.data?.filter((plan: PlanResponseDto) => plan.id !== deletedId) || [],
          pageInfo: {
            ...data.pageInfo,
            endOffset: data.pageInfo?.endOffset ? data.pageInfo.endOffset - 1 : 0,
          },
        }
      })
    },
  })
}

export const useActivatePlan = () => {
  const queryClient = useQueryClient()

  return useMutation<PlanResponseDto, Error, string>({
    mutationFn: activatePlan,
    onSuccess: (updatedPlan, id) => {
      // Update the specific plan detail
      queryClient.setQueryData(queryKeys.plans.detail(id), updatedPlan)

      // Update all plan lists by updating the specific plan
      queryClient.setQueriesData({ queryKey: queryKeys.plans.lists() }, (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object') return oldData
        const data = oldData as { data?: PlanResponseDto[] }

        return {
          ...data,
          data:
            data.data?.map((plan: PlanResponseDto) =>
              plan.id === id ? { ...plan, isActive: true } : plan,
            ) || [],
        }
      })
    },
  })
}

export const useDeactivatePlan = () => {
  const queryClient = useQueryClient()

  return useMutation<PlanResponseDto, Error, string>({
    mutationFn: deactivatePlan,
    onSuccess: (updatedPlan, id) => {
      // Update the specific plan detail
      queryClient.setQueryData(queryKeys.plans.detail(id), updatedPlan)

      // Update all plan lists by updating the specific plan
      queryClient.setQueriesData({ queryKey: queryKeys.plans.lists() }, (oldData: unknown) => {
        if (!oldData || typeof oldData !== 'object') return oldData
        const data = oldData as { data?: PlanResponseDto[] }

        return {
          ...data,
          data:
            data.data?.map((plan: PlanResponseDto) =>
              plan.id === id ? { ...plan, isActive: false } : plan,
            ) || [],
        }
      })
    },
  })
}

export const usePlanTenants = (id: string, filters?: Record<string, unknown>) => {
  return useQuery({
    queryKey: queryKeys.plans.tenants(id, filters),
    queryFn: () => getPlanTenants(id, filters),
    ...queryOptions.plans,
    enabled: !!id,
  })
}

export const useMyQpaServices = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.plans.myQpaServices(),
    queryFn: getMyQpaServices,
    ...queryOptions.plans,
    enabled: options?.enabled !== false,
  })
}
